import { message } from "antd";
import SettingsModel from "../lib/firebase/settingsModel";

/**
 * States that require High-Compliance Mode to stay ON (aligned with backend `HIGH_RISK_STATES` / MHMDA-style rules).
 * @type {readonly string[]}
 */
export const HIGH_COMPLIANCE_LOCK_STATE_CODES = ["WA", "NV", "CT", "IN", "KY"];

const FULL_STATE_NAME_TO_CODE = {
  washington: "WA",
  nevada: "NV",
  connecticut: "CT",
  indiana: "IN",
  kentucky: "KY",
};

/**
 * Normalize user-entered state to a 2-letter code, or '' if unknown.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeStateToCode(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const upper = s.toUpperCase();
  if (upper.length === 2 && /^[A-Z]{2}$/.test(upper)) return upper;
  const key = s.toLowerCase().replace(/\s+/g, " ");
  if (FULL_STATE_NAME_TO_CODE[key]) return FULL_STATE_NAME_TO_CODE[key];
  return "";
}

function extractPropertyStateCode(p) {
  if (!p || typeof p !== "object") return "";
  if (typeof p.address === "string") {
    const fromLine = parseStateFromFreeformAddress(p.address);
    if (fromLine) return fromLine;
  }
  return normalizeStateToCode(
    p.address?.state ?? p.state ?? p.addressState ?? ""
  );
}

/**
 * @param {Record<string, unknown>} unit
 * @param {Record<string, unknown>} [propertyById]
 * @returns {string}
 */
export function extractUnitStateCode(unit, propertyById) {
  if (!unit || typeof unit !== "object") return "";
  const direct = normalizeStateToCode(
    unit.address?.state ?? unit.address_state ?? unit.state ?? ""
  );
  if (direct) return direct;
  const pid = unit.propertyId;
  if (pid && propertyById && propertyById[pid]) {
    const fromProp = extractPropertyStateCode(propertyById[pid]);
    if (fromProp) return fromProp;
  }
  if (typeof unit.address === "string") {
    return parseStateFromFreeformAddress(unit.address);
  }
  return "";
}

function parseStateFromFreeformAddress(s) {
  if (!s || typeof s !== "string") return "";
  const upper = s.toUpperCase();
  for (const code of HIGH_COMPLIANCE_LOCK_STATE_CODES) {
    if (new RegExp(`\\b${code}\\b`).test(upper)) return code;
  }
  for (const [name, code] of Object.entries(FULL_STATE_NAME_TO_CODE)) {
    if (upper.includes(name.replace(/\s+/g, " ").toUpperCase())) return code;
  }
  return "";
}

/**
 * True if any property or door (unit) is in a mandatory high-compliance state.
 * @param {Array<Record<string, unknown>>} [properties]
 * @param {Array<Record<string, unknown>>} [units]
 * @returns {boolean}
 */
export function accountRequiresLockedHighCompliance(
  properties = [],
  units = []
) {
  const lock = new Set(HIGH_COMPLIANCE_LOCK_STATE_CODES);
  const propertyById = Object.fromEntries(
    (properties || []).filter(Boolean).map((p) => [p.id, p])
  );
  for (const p of properties || []) {
    const c = extractPropertyStateCode(p);
    if (c && lock.has(c)) return true;
  }
  for (const u of units || []) {
    const c = extractUnitStateCode(u, propertyById);
    if (c && lock.has(c)) return true;
  }
  return false;
}

/**
 * Detect if an address or state indicates Washington (WA).
 * @param {string} [address] - Full address string (e.g. "123 Main St, Seattle, WA 98101")
 * @param {string} [state] - State code or name (e.g. "WA" or "Washington")
 * @returns {boolean}
 */
export function isWashingtonAddress(address, state) {
  const s = (state || "").trim().toUpperCase();
  if (s === "WA" || s === "WASHINGTON") return true;
  if (!address || typeof address !== "string") return false;
  const a = address.trim();
  // ", WA " or " WA " or "Washington" or zip 98xxx
  if (/\bWA\b/i.test(a) || /\bWashington\b/i.test(a)) return true;
  if (/,?\s*98\d{3}\b/.test(a)) return true; // WA zips 98xxx
  return false;
}

/**
 * If the given address/state indicates Washington, enable High-Compliance Mode for the user
 * and show the required toast. Idempotent (safe to call every time address is saved).
 * @param {string} userId - Landlord/user UID
 * @param {string} [address] - Full address string
 * @param {string} [state] - State code or name
 * @param {{ invalidateSettings?: (userId: string) => Promise<void> }} [options] - e.g. pass queryClient invalidate for settings
 */
export async function ensureWashingtonHighCompliance(
  userId,
  address,
  state,
  options = {}
) {
  if (!userId || !isWashingtonAddress(address, state)) return;
  try {
    await SettingsModel.updateSettings(userId, { highComplianceMode: true });
    message.success(
      "Washington address detected: High-Compliance Mode enabled for legal safety."
    );
    if (typeof options.invalidateSettings === "function") {
      await options.invalidateSettings(userId);
    }
  } catch (e) {
    console.warn("[compliance] Failed to enable High-Compliance Mode:", e);
    message.warning(
      "Washington address detected. Please enable High-Compliance Mode in Settings for legal compliance."
    );
  }
}
